import { type StateCreator } from "zustand";
import { nextTokenStamp, type StampedTokens } from "../auth-persist.ts";

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  /** Epoch-ms stamp of the last set of the pair above — the only fact that
   *  orders two contexts' views of one session. Zero until the first set, and
   *  on a bag persisted before stamping existed. */
  rotatedAt: number;
  user: { id: string; email: string; role: string; emailVerified: boolean } | null;
  isAuthenticated: boolean;
  mfaSessionToken: string | null;
  mfaPending: boolean;
  mfaMethods: string[];
}

export interface AuthActions {
  setAuth: (token: string, user: AuthState["user"], refreshToken?: string) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  setExpiresAt: (expiresAt: number | null) => void;
  /** Takes a sibling context's rotation VERBATIM, stamp included. Never
   *  re-stamps: the pair already carries the moment it was minted, and
   *  restamping it here would make an adoption outrank the rotation it copied. */
  adoptTokens: (tokens: StampedTokens) => void;
  /** Records that the signed-in address is now confirmed. Additive and
   *  deliberately NOT stamped: only a token rotation may move the persist
   *  guard's stamp, and this moves no token. A no-op when there is no user to
   *  record it on, or when the flag already says so — the returned state is the
   *  same object, so no subscriber is woken. */
  setEmailVerified: (verified: boolean) => void;
  clearAuth: () => void;
  setMfaChallenge: (sessionToken: string, methods: string[]) => void;
  clearMfaChallenge: () => void;
}

export type AuthSlice = AuthState & AuthActions;

export const createAuthSlice: StateCreator<AuthSlice, [], [], AuthSlice> = (set) => ({
  token: null,
  refreshToken: null,
  expiresAt: null,
  rotatedAt: 0,
  user: null,
  isAuthenticated: false,
  mfaSessionToken: null,
  mfaPending: false,
  mfaMethods: [],
  // Every set of the pair stamps it, so a persist write can be ordered against
  // what is already in storage. `setExpiresAt` deliberately does NOT: it rides
  // immediately behind `setAuth` on the same rotation and a second stamp would
  // claim a rotation that never happened.
  setAuth: (token, user, refreshToken) =>
    set({
      token,
      user,
      isAuthenticated: true,
      refreshToken: refreshToken ?? null,
      rotatedAt: nextTokenStamp(),
    }),
  setRefreshToken: (refreshToken) => set({ refreshToken, rotatedAt: nextTokenStamp() }),
  setExpiresAt: (expiresAt) => set({ expiresAt }),
  setEmailVerified: (verified) =>
    set((state) => {
      if (state.user === null || state.user.emailVerified === verified) return state;
      return { user: { ...state.user, emailVerified: verified } };
    }),
  adoptTokens: ({ accessToken, refreshToken, expiresAt, rotatedAt }) =>
    set({ token: accessToken, refreshToken, expiresAt, rotatedAt, isAuthenticated: true }),
  // Stamped like any other set: a sign-out must OUTRANK the rotation it ends,
  // or the guard would restore the tokens it just cleared.
  clearAuth: () =>
    set({
      token: null,
      refreshToken: null,
      expiresAt: null,
      rotatedAt: nextTokenStamp(),
      user: null,
      isAuthenticated: false,
      mfaSessionToken: null,
      mfaPending: false,
      mfaMethods: [],
    }),
  setMfaChallenge: (sessionToken, methods) =>
    set({ mfaSessionToken: sessionToken, mfaPending: true, mfaMethods: methods }),
  clearMfaChallenge: () => set({ mfaSessionToken: null, mfaPending: false, mfaMethods: [] }),
});
